/**
 * Drop-in replacement for react-reveal's <Fade> and <Slide>.
 * Uses IntersectionObserver instead of scroll events, which fires
 * reliably on iOS Safari during momentum scrolling (unlike window.scroll).
 */
import React, { useRef, useState, useEffect } from "react";

function FadeReveal({ children }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Fall back to instant visibility if browser lacks IntersectionObserver
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect(); // Reveal once, never re-hide
        }
      },
      { threshold: 0.05 } // Trigger when 5% of element is visible
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(20px)",
        transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
        willChange: visible ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// Named exports so files can keep `import {Fade} from "..."` unchanged
export const Fade = FadeReveal;
export const Slide = FadeReveal;
